import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { saveEventItem } from '../utils/firebaseFunctions';
import Loader from './Loader';
import Sidebar from './Sidebar';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config';
import {
    collection,
    getDocs,
    deleteDoc,
    doc,
    setDoc,
} from 'firebase/firestore';
import { Pencil, Trash2 } from 'lucide-react';
import moment from 'moment';

const Events = () => {
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [Heading, setHeading] = useState('');
    const [Description, setDescription] = useState('');
    const [EventDate, setEventDate] = useState(getTodayDate());
    const [isLoading, setIsLoading] = useState(false);
    const [field, setField] = useState(false);
    const [alertStatus, setAlertStatus] = useState('danger');
    const [msg, setMsg] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [eventList, setEventList] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editEventData, setEditEventData] = useState(null);

    // --- NEW STATES FOR FILTERING ---
    const [filterDate, setFilterDate] = useState(''); // State for date filter input
    const [filterHeading, setFilterHeading] = useState(''); // State for heading filter input
    const [filteredEvents, setFilteredEvents] = useState([]); // State for filtered events

    // --- New States for Edit Image Handling ---
    const [editUploadedImageUrls, setEditUploadedImageUrls] = useState([]);
    const [editUploadProgress, setEditUploadProgress] = useState(0);

    const toggleSidebar = (state) => {
        setIsSidebarOpen(state);
    };

    const cloudName = "dabhatz7m";
    const uploadPreset = "ImageUpload";

    const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);

    // --- Reusable Image Upload Handler ---
    const handleImageUpload = (e, isEdit = false) => {
        e.preventDefault();
        const file = e.target.files[0]; // Use e.target.files for simplicity

        if (!file) return alert("Please select an image!");

        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);
        formData.append('folder', 'EventImages'); // Store in "EventImages" folder in Cloudinary

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, true);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;
                if (isEdit) {
                    setEditUploadProgress(percentComplete);
                } else {
                    setUploadProgress(percentComplete);
                }
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                if (!data.secure_url) {
                    console.error("Secure URL is missing from Cloudinary response");
                    return;
                }
                const imageUrl = data.secure_url;

                if (isEdit) {
                    setEditUploadedImageUrls((prevUrls) => [...prevUrls, imageUrl]);
                    setEditUploadProgress(0);
                } else {
                    setUploadedImageUrls((prevUrls) => [...prevUrls, imageUrl]);
                    setUploadProgress(0);
                }
            } else {
                console.error("Failed to upload image to Cloudinary");
                if (isEdit) {
                    setEditUploadProgress(0);
                } else {
                    setUploadProgress(0);
                }
            }
        };

        xhr.onerror = () => {
            console.error("Error uploading image:", xhr.statusText);
            if (isEdit) {
                setEditUploadProgress(0);
            } else {
                setUploadProgress(0);
            }
        };

        xhr.send(formData);
    };

    // --- Reusable Image Deletion Handler ---
    const handleDeleteImage = (index, isEdit = false) => {
        if (isEdit) {
            const updatedImages = [...editUploadedImageUrls];
            updatedImages.splice(index, 1);
            setEditUploadedImageUrls(updatedImages);
        } else {
            const updatedImages = [...uploadedImageUrls];
            updatedImages.splice(index, 1);
            setUploadedImageUrls(updatedImages);
        }
    };

    const saveDetails = async () => {
        setIsLoading(true);
        if (!Heading || !Description) { // Combined check
            setField(true);
            setMsg("Required fields can't be empty");
            setAlertStatus("danger");
            toast.error("Required fields can't be empty");
            setTimeout(() => {
                setField(false);
                setIsLoading(false);
            }, 4000);
            return;
        }

        // Image Validation
        if (uploadedImageUrls.length === 0) {
            setField(true);
            setMsg("Please upload at least one image.");
            setAlertStatus("danger");
            toast.error("Please upload at least one image.");
            setIsLoading(false);
            setTimeout(() => {
                setField(false);
            }, 4000);
            return;
        }
        try {
            const id = `${Date.now()}`;

            const data = {
                id: id,
                Heading,
                Description,
                EventDate,
                imageUrls: [...uploadedImageUrls],
            };
            await saveEventItem(data);
            setIsLoading(false);
            setField(true);
            setMsg("Details saved successfully");
            toast.success("Details saved successfully");
            clearData();
            fetchEvent(); // Re-fetch events after saving to update the table

            setTimeout(() => {
                setField(false);
            }, 4000);
        } catch (error) {
            console.error("Error saving details:", error);
            toast.error("Failed to save details");
            setIsLoading(false);
        }
    };

    const clearData = () => {
        setHeading('');
        setDescription('');
        setEventDate(getTodayDate());
        setUploadedImageUrls([]);
    };

    const fetchEvent = async () => {
        try {
            const querySnapshot = await getDocs(collection(firestore, 'Event'));
            const event = [];
            querySnapshot.forEach((doc) => {
                event.push({ ...doc.data(), docId: doc.id });
            });
            setEventList(event);
            setFilteredEvents(event); // Initialize filteredEvents with all events
        } catch (error) {
            console.error("Error fetching events:", error);
        }
    };

    const handleDeleteEvent = async (docId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this item? This action cannot be undone.");
        if (!confirmDelete) {
            return; // If the user cancels, stop the function
        }

        try {
            await deleteDoc(doc(firestore, 'Event', docId));
            toast.success("Deleted successfully");
            fetchEvent();
        } catch (error) {
            console.error("Error deleting events:", error);
            toast.error("Failed to delete");
        }
    };

    const openEditModal = (EventItem) => {
        setEditEventData({ ...EventItem });
        setEditUploadedImageUrls([...EventItem.imageUrls]);
        setEditModalOpen(true);
    };

    const handleUpdateEvent = async () => {
        if (!editEventData.Heading || !editEventData.Description) {
            toast.error("Please fill in all required fields.");
            return;
        }
        if (editUploadedImageUrls.length === 0) {
            toast.error("Please upload at least one image for the Event.");
            return;
        }

        try {
            const ref = doc(firestore, 'Event', editEventData.docId);
            await setDoc(ref, {
                id: editEventData.id,
                Heading: editEventData.Heading,
                Description: editEventData.Description,
                EventDate: editEventData.EventDate,
                imageUrls: editUploadedImageUrls,
            });
            toast.success("Event updated successfully");
            setEditModalOpen(false);
            fetchEvent();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update event");
        }
    };

    // --- EFFECT HOOK FOR FILTERING ---
    useEffect(() => {
        const applyFilters = () => {
            let tempEvents = [...eventList];

            // Filter by Date
            if (filterDate) {
                tempEvents = tempEvents.filter(item => item.EventDate === filterDate);
            }

            // Filter by Heading
            if (filterHeading) {
                const searchLower = filterHeading.toLowerCase();
                tempEvents = tempEvents.filter(item =>
                    item.Heading.toLowerCase().includes(searchLower)
                );
            }
            setFilteredEvents(tempEvents);
        };

        applyFilters(); // Apply filters whenever filterDate, filterHeading, or eventList changes
    }, [filterDate, filterHeading, eventList]);

    useEffect(() => {
        fetchEvent();
    }, []);

    return (
        <div className='lg:flex h-screen bg-white'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 p-1 lg:p-12 overflow-y-auto transition-all duration-300 lg:mt-2 mt-16 ${isSidebarOpen ? 'ml-56 p-0' : 'lg:ml-20 sm:ml-2 '}`}>
                <div className={`w-full max-w-4xl mx-auto bg-white shadow-xl rounded-2xl lg:p-8 ${isSidebarOpen ? ' p-1' : 'lg:p-8 p-8'}`}>
                    <h1 className={`lg:text-2xl font-bold text-[#fcaf17] mb-6 ${isSidebarOpen ? 'text-[13px]' : ''}`}>Add Events</h1>

                    {field && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`p-3 rounded-lg text-center text-lg font-medium ${alertStatus === "danger" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                        >
                            {msg}
                        </motion.p>
                    )}

                    <div className='lg:space-y-3 space-y-1'>
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='Date'> Date</label>
                            <input
                                id='EventDate'
                                type='date'
                                value={EventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg lg:text-md text-sm text-gray-800 focus:ring-1 focus:ring-blue-200 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>

                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='Name'>Heading</label>
                            <input
                                id='Heading'
                                type='text'
                                value={Heading}
                                autoComplete='off'
                                placeholder='Enter Heading'
                                onChange={(e) => setHeading(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>


                        <div>
                            <label
                                className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                                htmlFor="Description"
                            >
                                Description
                            </label>
                            <textarea
                                id="Description"
                                value={Description}
                                autoComplete="off"
                                placeholder="Enter Description"
                                onChange={(e) => setDescription(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                            />
                        </div>


                        <div className="mb-4">
                            <label
                                className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                                htmlFor="Description"
                            >
                                Image Upload
                            </label>
                            <label
                                htmlFor="imageInput"
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <svg
                                    className="mr-2 h-5 w-5 text-[#fcaf17]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span>Upload Images</span>
                            </label>
                            <input
                                type="file"
                                id="imageInput"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, false)} // Pass isEdit as false
                                multiple
                                className="hidden" // Hide the default input
                            />
                        </div>

                        {uploadProgress > 0 && (
                            <div className="mt-2">
                                <div className="bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                                <p className="text-sm text-gray-600 text-center">{Math.round(uploadProgress)}%</p>
                            </div>
                        )}
                        <div id="imagesContainer" className="flex flex-wrap gap-4 mt-2">
                            {uploadedImageUrls.map((url, index) => (
                                <div
                                    key={index}
                                    className="relative w-[200px] h-[130px] border rounded-md overflow-hidden"
                                >
                                    <img
                                        src={url}
                                        alt={`Uploaded ${index}`}
                                        className="w-full h-full object-cover"
                                    />
                                    <button
                                        onClick={() => handleDeleteImage(index, false)} // Pass isEdit as false
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                        title="Remove image"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-start">
                            <button
                                className="bg-blue-600 text-white lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300"
                                onClick={saveDetails}
                            >
                                {isLoading ? <Loader /> : 'Save '}
                            </button>
                        </div>

                    </div>
                </div>

                {/* --- Table Section - Conditional Rendering --- */}
                {eventList.length > 0 ? (
                    <div className="mt-12 p-8 transform transition-all duration-300 hover:shadow-yellow-300/40">
                        <h2 className="text-3xl font-extrabold mb-8 text-[#fcaf17] tracking-wide flex items-center gap-3 animate-fade-in-down">
                            <span>✨</span> Event Collection
                        </h2>

                        {/* --- FILTER INPUTS ADDED HERE --- */}
                       <div className="mb-8 p-6 bg-white rounded-lg shadow-md flex flex-col sm:flex-row sm:items-end sm:gap-6">
    {/* Filter by Date */}
    <div className="flex flex-col gap-2 mb-4 sm:mb-0">
        <label htmlFor="filterDate" className="font-semibold text-gray-800 text-sm">
            Filter by Date:
        </label>
        <input
            type="date"
            id="filterDate"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out text-gray-700 w-full sm:w-auto"
        />
    </div>

    {/* Filter by Heading */}
    <div className="flex flex-col gap-2">
        <label htmlFor="filterHeading" className="font-semibold text-gray-800 text-sm">
            Filter by Heading:
        </label>
        <div className="relative">
            <input
                type="text"
                id="filterHeading"
                value={filterHeading}
                onChange={(e) => setFilterHeading(e.target.value)}
                placeholder="Search by heading..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out text-gray-700 w-full sm:w-auto"
            />
            {/* Search Icon (example, you might use an SVG or a library like Heroicons) */}
            <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
            </svg>
        </div>
    </div>
</div>

                        <div className="overflow-hidden border border-yellow-200 transform transition-all duration-300 hover:shadow-yellow-200/20">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200/70 backdrop-blur-sm border-b border-yellow-300 text-[14px] uppercase font-bold tracking-wider text-orange-800 animate-slide-in-top">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4">Heading</th>
                                        <th className="px-6 py-4">Description</th>
                                        <th className="px-6 py-4">Image</th>
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* RENDER FILTERED EVENTS */}
                                    {filteredEvents.length > 0 ? (
                                        filteredEvents.map((item, index) => (
                                            <tr
                                                key={item.docId}
                                                className="group bg-white/70 transition-all duration-300 hover:bg-orange-50/70 border-b border-orange-100 last:border-b-0 animate-fade-in"
                                            >
                                                <td className="px-6 py-4 font-semibold text-gray-800 text-base">
                                                    {index + 1}
                                                </td>
                                                <td className="px-6 py-4 text-gray-900 font-medium">
                                                    {moment(item.EventDate, 'YYYY-MM-DD').format('Do MMMM YYYY')}
                                                </td>

                                                <td className="px-6 py-4 text-gray-900 font-medium">{item.Heading}</td>
                                                <td className="px-6 py-4 text-gray-600 text-sm leading-relaxed max-w-sm overflow-hidden text-ellipsis whitespace-nowrap">
                                                    {item.Description}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <img
                                                        src={item.imageUrls[0]}
                                                        alt={`Slider ${index + 1}`}
                                                        className="w-28 h-20 object-cover rounded-xl border-2 border-gray-200 shadow-md group-hover:scale-105 group-hover:border-orange-400 transition-all duration-300 ease-in-out cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-center flex justify-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(item)}
                                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                                                        title="Edit Event"
                                                    >
                                                        <Pencil size={16} />
                                                        Edit
                                                    </button>

                                                    <button
                                                        onClick={() => handleDeleteEvent(item.docId)}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                                                        title="Remove Slider"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Remove
                                                    </button>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500 italic">
                                                No events match your filter criteria.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12 p-8 text-center text-gray-500 text-lg italic bg-white border border-orange-100 rounded-xl shadow-sm">
                        No beautiful events to display yet. Let's create one!
                    </div>
                )}
                {/* --- Edit Modal --- */}
                {editModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-lg">
                            <h2 className="text-xl font-bold text-[#ea764d] mb-4">Edit Event</h2>

                            <label className="block mb-2 text-sm font-medium text-gray-700">Date</label>
                            <input
                                type="date"
                                value={editEventData?.EventDate || ''}
                                onChange={(e) => setEditEventData({ ...editEventData, EventDate: e.target.value })}
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                            />

                            <label className="block mb-2 text-sm font-medium text-gray-700">Heading</label>
                            <input
                                type="text"
                                value={editEventData?.Heading || ''}
                                onChange={(e) => setEditEventData({ ...editEventData, Heading: e.target.value })}
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                            />

                            <label className="block mb-2 text-sm font-medium text-gray-700">Description</label>
                            <textarea
                                value={editEventData?.Description || ''}
                                onChange={(e) => setEditEventData({ ...editEventData, Description: e.target.value })}
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                            />

                            {/* --- Image Upload Section for Edit Modal --- */}
                            <div className="mb-4">
                                <label
                                    htmlFor="editImageInput"
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                                >
                                    <svg
                                        className="mr-2 h-5 w-5 text-[#ea764d]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span>Upload New Image(s)</span>
                                </label>
                                <input
                                    type="file"
                                    id="editImageInput"
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, true)} // Pass isEdit as true
                                    multiple
                                    className="hidden"
                                />
                            </div>

                            {editUploadProgress > 0 && (
                                <div className="mt-2">
                                    <div className="bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${editUploadProgress}%` }}></div>
                                    </div>
                                    <p className="text-sm text-gray-600 text-center">{Math.round(editUploadProgress)}%</p>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-3 mb-4">
                                {editUploadedImageUrls?.map((url, idx) => (
                                    <div key={idx} className="relative w-[100px] h-[80px] border rounded overflow-hidden">
                                        <img src={url} alt={`edit-img-${idx}`} className="w-full h-full object-cover" />
                                        <button
                                            onClick={() => handleDeleteImage(idx, true)} // Pass isEdit as true
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                            title="Remove image"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {/* --- End Image Upload Section for Edit Modal --- */}


                            <div className="flex justify-end gap-4">
                                <button
                                    onClick={() => setEditModalOpen(false)}
                                    className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateEvent}
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* You might need to define these keyframe animations in your global CSS or a dedicated CSS file if you haven't already */}
                <style jsx>{`
                    @keyframes fade-in-down {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes slide-in-top {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes fade-in {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }

                    .animate-fade-in-down {
                        animation: fade-in-down 0.8s ease-out forwards;
                    }

                    .animate-slide-in-top {
                        animation: slide-in-top 0.6s ease-out forwards;
                    }

                    .animate-fade-in {
                        animation: fade-in 0.5s ease-out forwards;
                    }
                `}</style>
            </div >
        </div>

    );
};

export default Events;